import { connect } from 'react-redux';
import { useField } from 'react-final-form';

import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';
import { ErknmActExtract } from 'KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActExtract/ErknmActExtract';
import { ErknmActGeneral } from 'KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActGeneral/ErknmActGeneral';
import { ErknmActParticipants } from 'KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActParticipants/ErknmActParticipants';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { ErknmActEvents } from 'KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActEvents/ErknmActEvents';
import {
  getDecisionDataId,
  getDecisionFormRef,
  getDecisionOnSaveButtonClick,
  getKnmIsDecisionStatusFillInProcess,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { ErknmActIntroductions } from 'KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActIntroductions/ErknmActIntroductions';
import { ErknmActRemarks } from 'KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActRemarks/ErknmActRemarks';
import { ErknmActAdditional } from 'KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActAdditional/ErknmActAdditional';
import { ErknmActSignInfo } from 'KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActSignInfo/ErknmActSignInfo';
import { ErknmActSignButton } from 'KnmErknmPackage/ErknmActsModule/components/ErknmPaperlessActComponents/components/ErknmActSignButton/ErknmActSignButton';
import { IDecisionOrganizationForm } from 'DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { DecisionViolationEvidence } from 'DecisionPackage/DecisionViolationEvidenceModule/components/DecisionViolationEvidence';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { DecisionActViolations } from './DecisionActViolations/DecisionActViolations';
import { DecisionActSubject } from './DecisionActSubject/DecisionActSubject';
import { IKnmErknmFormObject } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';

interface IDecisionPaperlessActItemProps {
  value: IDecisionActForm;
  watchOnly: boolean;
  name: string;
  inspectors: IKnmErknmFormInspector[] | undefined;
  knoOrganization: IDictionaryKnoOrganizationSelectOption | undefined;
  supervisionType: IDictionarySupervisionTypeSelectOption | undefined;
  objects?: IKnmErknmFormObject[];
  organization: IDecisionOrganizationForm | undefined;
  isViolationFieldsWatchOnly: boolean;
  isDisabledByDirty?: boolean;
  created: string | undefined;
  basisDocumentDateTime: string | undefined;
}

interface IDecisionPaperlessActItemStoreProps {
  formRef: IDecisionFormRef | undefined;
  onSaveButtonClick: (() => void) | undefined;
  settings: IAdminSetting[];
  isDecisionStatusFillInProcess: boolean;
  decisionId: number | undefined;
}

function DecisionPaperlessActItemWithoutStore({
  value,
  watchOnly,
  name,
  inspectors,
  formRef,
  knoOrganization,
  supervisionType,
  objects,
  onSaveButtonClick,
  organization,
  isViolationFieldsWatchOnly,
  created,
  isDisabledByDirty = false,
  settings,
  isDecisionStatusFillInProcess,
  decisionId,
  basisDocumentDateTime,
}: IDecisionPaperlessActItemProps & IDecisionPaperlessActItemStoreProps) {
  const isDisabledField = watchOnly || !!value.signed || isDisabledByDirty;
  const prefixId = 'act';

  const events = useField<IKnmErknmFormEvent[]>('events')?.input?.value;

  const isUseEvidence = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_EVIDENCE_4_1_25_STARTS_FROM,
  );
  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  const isLaterThanKnmValidationSetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_53_STARTS_FROM,
  );

  const reasons = formRef?.current.getFieldState('reasons')?.value;

  const isPrescriptionCheck = reasons?.[0].reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK;
  const decisionMinHoldingDateTime = isLaterThanKnmValidationSetting
    ? reasons?.[0].reason?.prescriptionDate || undefined
    : basisDocumentDateTime;

  return (
    <>
      <ErknmActExtract extract={value.document} withoutMarginTop />
      <ErknmActGeneral
        name={name}
        isDisabledField={isDisabledField}
        prefixId={prefixId}
        isDecision
        extractVersion={value.extractVersion}
        signActDateTime={value.signActDateTime}
        decisionMinHoldingDateTime={decisionMinHoldingDateTime}
      />
      <DecisionActSubject organization={organization} isDecision />
      <ErknmActParticipants
        id={prefixId}
        isDecision
        inspectorsErknm={inspectors}
        isDisabledField={isDisabledField}
        name={name}
        expertsErknm={[]}
        formRef={formRef}
      />
      {events.length > 0 && (
        <ErknmActEvents
          id={prefixId}
          name={name}
          isDisabledField={isDisabledField}
          isDecision
          formRef={formRef}
          isPaperless={value.paperless}
          value={value}
        />
      )}

      {isUseEvidence && (
        <DecisionViolationEvidence id={prefixId} name={name} formRef={formRef} isWatchOnly={isDisabledField} />
      )}
      <DecisionActViolations
        id={prefixId}
        watchOnly={isViolationFieldsWatchOnly}
        name={name}
        value={value}
        isDisabledField={isDisabledField}
        isDisabledByDirty={isDisabledByDirty}
        knoOrganization={knoOrganization}
        supervisionType={supervisionType}
        objects={objects}
        isEnablePrescriptionCheck={isEnablePrescriptionCheck}
        isPrescriptionCheck={isPrescriptionCheck}
      />
      <ErknmActSignInfo
        id={prefixId}
        isDisabledField={isDisabledField}
        name={name}
        inspectors={inspectors}
        isDecision
        formRef={formRef}
        value={value}
      />
      {value.document && (
        <>
          <ErknmActIntroductions
            isDecision
            id={`organization.${prefixId}`}
            name={name}
            formRef={formRef}
            isDisabledByDirty={isDisabledByDirty}
          />
          <ErknmActRemarks
            isDecision
            id={`organization.${prefixId}`}
            name={name}
            formRef={formRef}
            isDisabledByDirty={isDisabledByDirty}
            holdingDateTime={value.holdingDateTime}
          />
          {value.fileInvalidate && <ErknmActAdditional name={name} attachment={value.fileInvalidate} isDecision />}
        </>
      )}
      <ErknmActSignButton
        value={value}
        formRef={formRef}
        onSaveButtonClick={onSaveButtonClick}
        isDecision
        isStatusFillInProcess={isDecisionStatusFillInProcess}
        isEnablePrescriptionCheck={isEnablePrescriptionCheck}
        decisionId={decisionId}
        name={name}
      />
    </>
  );
}

const mapStateToProps = (state: IRootState): IDecisionPaperlessActItemStoreProps => {
  return {
    formRef: getDecisionFormRef(state),
    onSaveButtonClick: getDecisionOnSaveButtonClick(state),
    settings: getAdminFetchSettings(state),
    isDecisionStatusFillInProcess: getKnmIsDecisionStatusFillInProcess(state),
    decisionId: getDecisionDataId(state),
  };
};

export const DecisionPaperlessActItem = connect(mapStateToProps)(DecisionPaperlessActItemWithoutStore);
